import clsx from 'clsx';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { UnitOfMeasure } from '@/core/UnitOfMeasure.atom';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { itemIconClass } from '@/utilities/utilities';
import { faWarning } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, Select } from '@seeqdev/qomponents';
import type { GroupBase, MultiValue, SingleValue } from 'react-select';
import type { UnitOfMeasure as UOM } from '@/utilities/unit.utilities';

type SelectableItem = {
  id?: string;
  name?: string;
  selectGroup?: string;
  displayUnitOfMeasure?: UOM;
  color?: string;
  assets?: Array<{
    formattedName?: string;
    datasource?: { name?: string };
  }>;
  lane?: string | number;
};

type BivariantCallback<T> = {
  bivarianceHack(arg: T): void;
}['bivarianceHack'];

interface SelectItemWrapperProps<TOnChange = SelectableItem | SelectableItem[] | string | null> {
  items: SelectableItem[];
  selected: SelectableItem | string | Array<SelectableItem | string> | null | undefined;
  // for Asset Groups we need to compare the items to the group as well as the item ids will be the same for asset
  // column items
  selectByIdAndGroup?: boolean;
  allowClear: boolean;
  selectPlaceholder?: string;
  isMultipleSelect: boolean;
  disableAutoSelect?: boolean;
  loadingMetadata: boolean;
  noWrap: boolean;
  onRemove?: BivariantCallback<SelectableItem | string>;
  onChange: BivariantCallback<TOnChange>;
  isGrouped?: boolean;
  getCustomIcon?: (item: SelectableItem) => string;
  stopPropagation?: boolean;
  addAllEnabled?: boolean;
  testId?: string;
  showError?: boolean;
  showLane?: boolean;
  isDisabled?: boolean;
  extraClassNames?: string;
  closeMenuOnSelect?: boolean;
  onAddAllClick?: () => void;
  controlClassNames?: string;
  /** Position within InputGroup for border-radius styling */
  inputGroup?: 'left' | 'right';
}

export const SelectItemWrapper = <TOnChange = SelectableItem | SelectableItem[] | string | null,>(
  props: SelectItemWrapperProps<TOnChange>,
) => {
  const { t } = useTranslation();

  const {
    items,
    selected,
    allowClear,
    onChange,
    isMultipleSelect,
    onRemove,
    loadingMetadata,

    selectPlaceholder,
    noWrap = false,
    isGrouped = true,
    addAllEnabled = false,
    getCustomIcon = itemIconClass,
    stopPropagation = false,
    testId,
    showError = false,
    showLane = false,
    isDisabled = false,
    extraClassNames = '',
    closeMenuOnSelect = true,
    onAddAllClick,
    controlClassNames,
    inputGroup,
  } = props;

  // We have to close empty selection menu after "Add all".
  // To do this we simulate click event on selector container
  const closeSelectorMenu = () => {
    const mouseClickEvents = ['mousedown', 'click', 'mouseup'];

    const simulateMouseClick = (element: HTMLElement) => {
      mouseClickEvents.forEach((mouseEventType) =>
        element.dispatchEvent(
          new MouseEvent(mouseEventType, {
            view: window,
            bubbles: true,
            cancelable: true,
            buttons: 1,
          }),
        ),
      );
    };

    const element = document.querySelector('.specOpenIt');
    if (element instanceof HTMLElement) simulateMouseClick(element);
  };

  const getSelectedIds = () => {
    if (!Array.isArray(selected)) {
      return [] as string[];
    }

    return selected
      .map((entry) => (typeof entry === 'string' ? entry : entry?.id))
      .filter((id): id is string => typeof id === 'string');
  };

  const groupItems = (ungrouped: SelectableItem[]) => {
    const groupItem = (item: SelectableItem) => {
      switch (item?.selectGroup) {
        case 'assetGroupColumn':
          return 'ASSET_GROUP_EDITOR.ASSET_GROUP_COLUMNS';
        case 'original':
          return 'ASSET_GROUP_EDITOR.ORIGINAL_ASSIGNMENT';
        case 'pinned':
          return 'SEARCH_DATA.PINNED';
        case 'recentlyAccessed':
          return 'SEARCH_DATA.RECENTLY_ACCESSED';
        default:
          return 'DETAILS';
      }
    };
    return _.chain(ungrouped)
      .groupBy(groupItem)
      .map((obj) => {
        return { label: t(groupItem(obj[0])), options: obj };
      })
      .value();
  };

  const displayEntry = (item: SelectableItem | null | undefined, wrapperStyle: string) => {
    if (!item) {
      return null;
    }

    const unitOfMeasure = item.displayUnitOfMeasure;
    const className = clsx('tw:pr-sq-5', 'fa', 'sq-tool-description-color', getCustomIcon(item));

    if (isItemRedacted(item)) {
      return (
        <div className="flexColumnContainer tw:pt-2">
          <Icon icon={faWarning} type="danger" extraClassNames="pr5" />
          <div id="specSelectedSignal" className="flexRowContainer flexFill tw:pb-sq-1">
            <div className="flexColumnContainer specChoiceName font-size-smaller">{t('NO_ITEM_ACCESS')}</div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={wrapperStyle}>
          <Icon icon={className} type="color" color={item?.color || 'sq-text-color'} />
          <div
            id="specSelectedSignal"
            className={clsx('flexRowContainer flexFill selectItem_itemName tw:pb-sq-1', {
              'tw:whitespace-nowrap': noWrap,
              'aggressiveWordBreak': !noWrap,
            })}>
            <div className="flexColumnContainer tw:items-center">
              <strong className="specChoiceName tw:mr-sq-5">{item?.name} </strong>
              {unitOfMeasure ? <UnitOfMeasure unitOfMeasure={unitOfMeasure} /> : null}
            </div>

            <div className="xsmall itemAsset">
              {item?.assets?.[0]?.formattedName || item?.assets?.[0]?.datasource?.name}
            </div>
          </div>
          {showLane && item?.lane ? (
            <span className="specLane xsmall tw:flex-1 tw:pl-sq-5 tw:text-right">
              {t('SELECT_ITEM_LANE', { lane: item.lane })}
            </span>
          ) : null}
        </div>
      );
    }
  };

  const getSelectedItemLabel = (item: SelectableItem | string | null | undefined) => {
    const currentItem =
      typeof item === 'string'
        ? _.find(items, { id: item })
        : item
          ? (_.find(items, { id: item.id }) ?? item)
          : undefined;
    return displayEntry(currentItem, 'flexColumnContainer flexCenter flexFill');
  };

  const getOptionLabel = (item: SelectableItem) => {
    const wrapperClassName = clsx(
      'flexColumnContainer',
      'tw:items-center tw:justify-center',
      'cursorPointer',
      'tw:pl-sq-10',
      'tw:pr-sq-10',
      'tw:pt-0.5',
      'tw:pb-0.5',
    );
    return displayEntry(item, wrapperClassName);
  };

  const getOptionValue = (item: SelectableItem | string) => (typeof item === 'string' ? item : (item.id ?? ''));

  const renderGroupLabel = (data: { label: string; options: SelectableItem[] }) => {
    const groupStyles = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    };

    return (
      <div style={groupStyles}>
        <span>{data.label}</span>
        {isMultipleSelect && addAllEnabled && (
          <FakeLink
            testId="detailsAddAll"
            onClick={() => {
              onAddAllClick
                ? onAddAllClick()
                : data.options.forEach((item) => onChange([item] as unknown as TOnChange));
              closeSelectorMenu();
            }}>
            {t('DETAILS_SELECTION.ADD_ALL')}
          </FakeLink>
        )}
      </div>
    );
  };

  const prepareItemsForDisplay = (itemsList: SelectableItem[]) => {
    let displayItems = itemsList.filter((item) => !isItemRedacted(item));
    if (isMultipleSelect) {
      const selectedIds = getSelectedIds();
      if (selectedIds.length) {
        displayItems = displayItems.filter((item) => !selectedIds.includes(item.id ?? ''));
      }
    }
    return isGrouped ? groupItems(displayItems) : displayItems;
  };

  const matchesFilter = (option: { data: SelectableItem }, input: string) => {
    const optionName = typeof option.data.name === 'string' ? option.data.name : '';
    return input === '' || _.includes(_.toLower(optionName), _.toLower(input));
  };

  const displayItems = prepareItemsForDisplay(items);

  const handleSelectChange = (value: SingleValue<SelectableItem> | MultiValue<SelectableItem>) => {
    onChange(value as unknown as TOnChange);
  };

  const handleRemoveOption = onRemove
    ? (item: unknown) => {
        if (!item) {
          return;
        }
        if (typeof item === 'string') {
          onRemove(item);
          return;
        }
        if (typeof item === 'object') {
          onRemove(item as SelectableItem);
        }
      }
    : undefined;

  const normalizedSelected =
    typeof selected === 'undefined' ? null : (selected as SelectableItem | SelectableItem[] | string | null);

  return (
    <div
      data-testid={testId}
      className={`${isGrouped ? 'tw:items-center' : ''} ${
        isDisabled ? 'tw:cursor-not-allowed!' : ''
      } flexColumnContainer tw:flex-1`}
      onClick={(e) => stopPropagation && e.stopPropagation()}>
      <Select
        placeholder={selectPlaceholder ? t(selectPlaceholder) : t('SELECT_ITEM')}
        isClearable={allowClear}
        value={normalizedSelected as unknown}
        isMulti={isMultipleSelect}
        options={displayItems}
        formatGroupLabel={(group: GroupBase<unknown>) =>
          renderGroupLabel(group as unknown as { label: string; options: SelectableItem[] })
        }
        onChange={handleSelectChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        getSelectedValueLabel={getSelectedItemLabel}
        menuPortalTarget={document.body}
        isLoading={loadingMetadata}
        filterOption={matchesFilter}
        extraClassNames={`flexFill ${extraClassNames}`}
        showError={showError}
        onRemove={handleRemoveOption}
        isDisabled={isDisabled}
        closeMenuOnSelect={closeMenuOnSelect}
        id={`selectItemWrapper-${testId}`}
        controlClassNames={controlClassNames}
        inputGroup={inputGroup}
      />
    </div>
  );
};

export default SelectItemWrapper;
